// src/pages/Dashboard.js
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Spinner from "react-js-loader";

const API = "https://mocaub1h8d.execute-api.us-east-1.amazonaws.com/prod";

const GOLD = "#dfa66b";

function StatCard({ label, value, sub, bg = "#fff", color = "#111" }) {
  return (
    <div style={{ ...cardStyles.card, background: bg }}>
      <div style={{ fontSize: 32, fontWeight: "800", color }}>{value}</div>
      <div style={{ fontSize: 13, fontWeight: "600", color, opacity: 0.85, marginTop: 2 }}>{label}</div>
      {sub !== undefined && (
        <div style={{ fontSize: 11, color, opacity: 0.6, marginTop: 4 }}>{sub}</div>
      )}
    </div>
  );
}

function Bar({ label, value, max, color }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 13 }}>
        <span>{label}</span>
        <span style={{ fontWeight: "700" }}>{value} ({pct}%)</span>
      </div>
      <div style={{ background: "#e9ecef", borderRadius: 6, height: 10, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, background: color, height: "100%", borderRadius: 6, transition: "width 0.5s ease" }} />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [loading, setLoading] = useState(false);
  const [eventos, setEventos] = useState([]);
  const [selectedEvento, setSelectedEvento] = useState("");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.post(`${API}/getEventsSensoria`, { loginType: "1" });
        let lista = [];
        if (Array.isArray(res.data)) lista = res.data;
        else if (res.data?.body) lista = JSON.parse(res.data.body);
        lista.sort((a, b) => new Date(b.fechaEvento) - new Date(a.fechaEvento));
        setEventos(lista);
      } catch {}
    };
    load();
  }, []);

  const loadStats = useCallback(async (eventoId) => {
    setLoading(true);
    setStats(null);
    try {
      // Intenta endpoint dedicado primero; si no existe, calcula desde inscripciones
      let data = null;
      try {
        const res = await axios.post(`${API}/getDashboardStats`, { idEvento: eventoId });
        if (Array.isArray(res.data)) data = res.data[0];
        else if (res.data?.body) data = JSON.parse(res.data.body);
        else data = res.data;
      } catch {
        // fallback: calcular desde getInscripciones
        const res2 = await axios.post(`${API}/getInscripciones`, { idEvento: eventoId });
        let guests = [];
        if (Array.isArray(res2.data)) guests = res2.data;
        else if (res2.data?.body) guests = JSON.parse(res2.data.body);
        data = computeStats(guests);
      }
      setStats(data);
    } catch {
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedEvento) loadStats(selectedEvento);
    else setStats(null);
  }, [selectedEvento, loadStats]);

  return (
    <div style={styles.page}>
      {loading && (
        <div style={styles.loaderOverlay}>
          <Spinner type="bubble-top" bgColor={GOLD} color={GOLD} size={90} />
        </div>
      )}

      <div style={styles.header}>
        <h2 style={{ margin: 0, fontSize: 20 }}>Dashboard</h2>
        <select
          style={styles.eventoSelect}
          value={selectedEvento}
          onChange={(e) => setSelectedEvento(e.target.value)}
        >
          <option value="">Seleccioná un evento...</option>
          {eventos.map((ev) => (
            <option key={ev.idEvento} value={ev.idEvento}>{ev.nombre}</option>
          ))}
        </select>
      </div>

      {!selectedEvento && (
        <p style={{ textAlign: "center", color: "#888", padding: 50 }}>
          Seleccioná un evento para ver las estadísticas.
        </p>
      )}

      {stats && (
        <div style={styles.content}>
          {/* Cards principales */}
          <div style={styles.cardsGrid}>
            <StatCard label="Total inscriptos" value={stats.total} bg="#1a1a1a" color="#dfa66b" />
            <StatCard label="Aprobados" value={stats.aprobados} bg="#d1e7dd" color="#0a5234"
              sub={`${stats.total ? Math.round((stats.aprobados / stats.total) * 100) : 0}% del total`} />
            <StatCard label="Pendientes" value={stats.pendientes} bg="#fff3cd" color="#856404" />
            <StatCard label="Rechazados" value={stats.rechazados} bg="#f8d7da" color="#842029" />
          </div>

          {/* Con/sin comprobante */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Comprobante de pago</h3>
            <Bar label="Con comprobante" value={stats.con_comprobante} max={stats.total} color="#198754" />
            <Bar label="Sin comprobante" value={stats.total - stats.con_comprobante} max={stats.total} color="#adb5bd" />
          </div>

          {/* Por sexo */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Por sexo</h3>
            <Bar label="Femenino" value={stats.gender_f} max={stats.total} color="#e91e8c" />
            <Bar label="Masculino" value={stats.gender_m} max={stats.total} color="#1976d2" />
            <Bar label="Otro" value={stats.gender_otro} max={stats.total} color="#9e9e9e" />
          </div>

          {/* Edad promedio */}
          {stats.age_avg > 0 && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Edad</h3>
              <div style={styles.ageRow}>
                <div style={styles.ageChip}>
                  <span style={{ fontSize: 26, fontWeight: "800", color: GOLD }}>{stats.age_avg}</span>
                  <span style={{ fontSize: 12, color: "#555" }}>Edad promedio</span>
                </div>
                <div style={styles.ageChip}>
                  <span style={{ fontSize: 26, fontWeight: "800", color: GOLD }}>{stats.age_min}</span>
                  <span style={{ fontSize: 12, color: "#555" }}>Mínima</span>
                </div>
                <div style={styles.ageChip}>
                  <span style={{ fontSize: 26, fontWeight: "800", color: GOLD }}>{stats.age_max}</span>
                  <span style={{ fontSize: 12, color: "#555" }}>Máxima</span>
                </div>
              </div>
            </div>
          )}

          {/* Rangos etarios */}
          {stats.age_ranges && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Rangos etarios</h3>
              {Object.entries(stats.age_ranges).map(([range, count]) => (
                <Bar key={range} label={range} value={count} max={stats.total} color={GOLD} />
              ))}
            </div>
          )}

          {/* Registros por día */}
          {stats.by_day && stats.by_day.length > 0 && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Registros por día</h3>
              {stats.by_day.map((d) => (
                <Bar key={d.date} label={d.date} value={d.count} max={Math.max(...stats.by_day.map((x) => x.count))} color="#6c757d" />
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{ height: 80 }} />
    </div>
  );
}

function calcAge(birth_date) {
  if (!birth_date) return null;
  const diff = Date.now() - new Date(birth_date).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
}

function computeStats(guests) {
  const total = guests.length;
  const aprobados = guests.filter((g) => g.status === "APROBADO").length;
  const pendientes = guests.filter((g) => g.status === "PENDIENTE").length;
  const rechazados = guests.filter((g) => g.status === "RECHAZADO").length;
  const con_comprobante = guests.filter((g) => g.payment_proof_url).length;
  const gender_f = guests.filter((g) => g.gender === "F").length;
  const gender_m = guests.filter((g) => g.gender === "M").length;
  const gender_otro = guests.filter((g) => g.gender === "Otro").length;

  const ages = guests.map((g) => calcAge(g.birth_date)).filter(Boolean);
  const age_avg = ages.length ? Math.round(ages.reduce((a, b) => a + b, 0) / ages.length) : 0;
  const age_min = ages.length ? Math.min(...ages) : 0;
  const age_max = ages.length ? Math.max(...ages) : 0;

  const age_ranges = {
    "< 18": ages.filter((a) => a < 18).length,
    "18-25": ages.filter((a) => a >= 18 && a <= 25).length,
    "26-35": ages.filter((a) => a >= 26 && a <= 35).length,
    "36-45": ages.filter((a) => a >= 36 && a <= 45).length,
    "> 45": ages.filter((a) => a > 45).length,
  };

  const dayCount = {};
  guests.forEach((g) => {
    const d = (g.created_at || "").slice(0, 10);
    if (d) dayCount[d] = (dayCount[d] || 0) + 1;
  });
  const by_day = Object.entries(dayCount)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, count]) => ({ date, count }));

  return { total, aprobados, pendientes, rechazados, con_comprobante, gender_f, gender_m, gender_otro, age_avg, age_min, age_max, age_ranges, by_day };
}

const styles = {
  page: { background: "#f4f4f4", minHeight: "100vh" },
  loaderOverlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
    backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999,
  },
  header: {
    background: "#1a1a1a", color: "#fff", padding: "16px 20px",
    display: "flex", flexDirection: "column", gap: 10,
  },
  eventoSelect: {
    padding: "10px 14px", borderRadius: 8, border: "none",
    fontSize: 14, background: "#333", color: GOLD,
  },
  content: { padding: "16px" },
  cardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 12,
    marginBottom: 20,
  },
  section: {
    background: "#fff", borderRadius: 14, padding: "16px 20px",
    marginBottom: 16, boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
  },
  sectionTitle: { margin: "0 0 14px", fontSize: 15, fontWeight: "700", color: "#1a1a1a" },
  ageRow: { display: "flex", gap: 12, justifyContent: "space-around" },
  ageChip: {
    display: "flex", flexDirection: "column", alignItems: "center",
    background: "#f8f8f8", borderRadius: 10, padding: "12px 20px", flex: 1,
  },
};

const cardStyles = {
  card: {
    borderRadius: 14, padding: "16px 20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    display: "flex", flexDirection: "column", alignItems: "center",
  },
};
